import { useParams, Link } from 'react-router-dom'
import { STACKS, getStackBySlug } from '../data/stacks.js'
import JumpNav from '../components/JumpNav.jsx'
import Related from '../components/Related.jsx'
import Seo from '../components/Seo.jsx'

export default function StackDetail() {
  const { slug } = useParams()
  const s = getStackBySlug(slug)

  if (!s) {
    return (
      <main className="page">
        <div className="container center" style={{ padding: '60px 0' }}>
          <h1 className="section-title">Stack not found</h1>
          <Link to="/stacks" className="btn btn-primary">Back to stacks</Link>
        </div>
      </main>
    )
  }

  const jump = [
    { id: 'overview', label: 'Overview' },
    { id: 'why', label: 'Why together' },
    { id: 'peptides', label: 'The peptides' },
    { id: 'cautions', label: 'Cautions' },
  ]

  // Other stacks for the "related" cards.
  const others = STACKS.filter((x) => x.slug !== s.slug).slice(0, 3)

  return (
    <main className="page">
      <Seo
        title={`${s.name} | CalculateMyPeptides`}
        description={`${s.summary} The ${s.name} peptide stack — the compounds involved, typical dosing, and how people run it. Educational, not medical advice.`}
        image="/og/stack.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Stacks', path: '/stacks' },
          { name: s.name, path: `/stacks/${s.slug}` },
        ]}
      />
      <div className="container page-narrow">
        <Link to="/stacks" className="pep-back">← Peptide stacks</Link>

        <div className="pep-hero">
          <span className="pep-cat">{s.tag}</span>
          <h1 className="pep-title">{s.icon} {s.name}</h1>
          <p className="pep-lede">{s.summary}</p>
        </div>

        <JumpNav items={jump} />

        <section className="pep-section" id="overview">
          <h2 className="pep-h2">Overview</h2>
          <p>{s.overview}</p>
        </section>

        <section className="pep-section" id="why">
          <h2 className="pep-h2">Why these are stacked together</h2>
          <ul className="pep-list">{s.whyTogether.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </section>

        <section className="pep-section" id="peptides">
          <h2 className="pep-h2">The peptides in this stack</h2>
          <div className="stack-peptides">
            {s.peptides.map((p) => (
              <Link key={p.slug} to={`/peptides/${p.slug}`} className="card stack-pep-card">
                <strong>{p.name}</strong>
                <span>Read the profile →</span>
              </Link>
            ))}
          </div>
          <p className="muted small" style={{ marginTop: 12 }}>
            Reconstitute each with the <Link to="/calculator">calculator</Link>, or — if they’re premixed in one
            vial — use the <Link to="/blend">blend calculator</Link>.
          </p>
        </section>

        <section className="pep-section" id="cautions">
          <h2 className="pep-h2">Cautions</h2>
          <ul className="pep-list warn-list">{s.cautions.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </section>

        <div className="pep-disclaimer">
          ⚠️ Educational only — not medical advice and not a recommendation to combine or use any compound. Many
          peptides referenced are research chemicals not approved for human use. Consult a qualified healthcare
          professional.
        </div>

        <Related
          title="Related stacks"
          links={[
            ...others.map((o) => ({ to: `/stacks/${o.slug}`, label: o.name, desc: o.tag })),
            { to: '/glossary', label: 'Glossary', desc: 'Look up any term' },
          ]}
        />
      </div>
    </main>
  )
}
